import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { type PlateInventoryEntry, parseLocaleNumber } from '@heraion/shared';

import { Chip } from '../primitives/Chip';
import { TextField } from '../primitives/TextField';
import type { useTheme } from './theme';
import {
  activeDenominations,
  formatUnitValue,
  formatUnitWeight,
  weightLocale,
} from './plateCalcMath';
import { unitWord, weightUnitLabels } from '../weightUnitLabels';
import type { makeStyles } from './plateCalcModalStyles';
import type { WeightUnit } from './state/loggerDraft';

// The plate calculator's CONFIGURATION block: which bar you're on, and which
// plates the gym has. Split out of PlateCalcModal (LG-37b fetta A) for two
// reasons that arrived together — the modal crossed the 500-line cap, and
// Antenore's correction to the 10/09 mockup moved both controls into one
// place: «barra e dischi nello stesso widget sotto il sunto, così l'utente non
// deve aprire un altro widget».
//
// ⛔ That correction is why there is no show/hide toggle here any more. The
// inventory used to sit behind an `editingInventory` fold; a second widget to
// open is exactly what he asked to remove, so the fold is GONE rather than
// defaulted open, and its two i18n keys went with it.
//
// ⚠️ NOT in this block: the loading shape. It stays up beside the target in the
// modal because it changes the RESULT — it's what you're calculating, not what
// equipment you own. That placement is my call and he can reverse it
// (timeline-logger.md §LG-37b).
//
// State lives in the modal, not here: `inventory` feeds `computePlates`, so a
// copy down here would be a second home for the calculator's own input. The
// ONE exception is the custom-bar draft below, which is view state by
// definition: it is what the athlete has typed so far and has no meaning until
// it parses.
//
// ⭐ LG-37b E2 — THE UNIT IS SAID ONCE PER SECTION, in the title, and the
// weights under it are bare. Antenore's call (11/09) out of the UX audit: in a
// pound gym the bar chip read "45 lb" while the plate chip beside it read
// "45", and the fix could not be the other direction — putting the suffix on
// every plate chip wraps that row, and «deve starci tutto su una linea» is his
// word of 10/09. ⇒ Every number in this block goes through `formatUnitValue`
// (bare); the unit lives in `barLabel`/`inventoryTitle`, interpolated as ONE
// token so German and Russian can put it where their grammar wants it.
//
// ⚠️ The ± buttons' SPOKEN labels keep the unit on the weight, and that is not
// an oversight: a screen reader reads those rows out of context, where "remove
// a pair — 25" names nothing. They still go through `formatUnitWeight`, whose
// suffix is the untranslated literal — a Russian hears "lb" under a title
// saying "фунт". That is `CN-26` ondata 2 (the unit leaves `formatWeight`),
// declared here rather than half-cured inside this slice.
//
// ⚠️ The ± buttons are deliberately still hand-rolled rather than the shared
// `Stepper`: Stepper renders a bare number, and this row's value has to read
// "4 pairs" — the pairs-vs-plates distinction is the whole point of this modal,
// so collapsing to it would LOSE the word. Registered as a duplication in
// next/consolidation.md instead of restyling a control he only asked to MOVE.

type Props = {
  theme: ReturnType<typeof useTheme>;
  styles: ReturnType<typeof makeStyles>;
  /** The bar, in the ROW's unit — 20 in a metric gym, 45 in a pound one. */
  bar: number;
  /** The bars this gym racks, in the same unit. Handed down rather than read
   *  from `plateConfig` here: the modal's state hook already picked the list
   *  for this unit, and a second read is a second answer to the same question. */
  barOptions: ReadonlyArray<number>;
  onSelectBar: (value: number) => void;
  inventory: ReadonlyArray<PlateInventoryEntry>;
  /** ⛔ Says how to WRITE the sizes, never how to convert them: a pound rack's
   *  25 is a 25 lb plate, not 25 kg re-expressed (`equipment-model.md` «L'invariante che sopravvive a tutto»). */
  unit: WeightUnit;
  onSetPairs: (size: number, pairs: number) => void;
};

export function PlateCalcInputsSection({
  theme,
  styles,
  bar,
  barOptions,
  onSelectBar,
  inventory,
  unit,
  onSetPairs,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = weightLocale(i18n);
  const unitLabels = weightUnitLabels(t);

  const pairsBySize = useMemo(() => {
    const map = new Map<number, number>();
    for (const e of inventory) map.set(e.size, e.pairs);
    return map;
  }, [inventory]);

  const unitLabel = unitWord(t, unit);
  const barLabel = t('mobile.plates.barLabel', { unit: unitLabel });

  // ⭐ LG-37b E2 — the custom bar. A leg press with 54 kg of empty carriage, a
  // hack squat, a sled: their tare is a property of THIS gym, which is why it
  // lives in `plateConfig` on the device and never in the catalog — and why
  // `schemas/exercise.ts:159`, which caps `defaultBarKg` at 50, does not touch
  // it. `persistPlateBar` has always accepted any finite value; until now
  // nothing could type one.
  //
  // ⛔ "Custom" is not a state we store — it is the ABSENCE of a matching
  // preset, derived on every render. A stored `isCustom` flag would be a second
  // answer to "which chip is selected", and the two would disagree the day the
  // preset list changes (which is exactly what this slice just did to it).
  //
  // ⚠️ Plain `includes`, no epsilon: every value that reaches `bar` has been
  // through `roundWeight(…, 'display')` — 3 decimals in kg, 1 in lb — so it is
  // a number `Intl` can write exactly, and a stored one round-trips through
  // `String`/`parseFloat` unchanged. An epsilon here would be a guard against a
  // hazard this path does not have.
  const isCustom = !barOptions.includes(bar);
  const [customOpen, setCustomOpen] = useState(false);
  const [customText, setCustomText] = useState('');

  // What the athlete typed, as a number. NaN while the field is empty or
  // nonsense, which is what drives both the error line and the refusal to
  // commit. `parseLocaleNumber` and not `parseFloat`: the keyboard is a
  // decimal-pad, so an Italian types "7,5" and `parseFloat` stops at the comma
  // and hands back 7 — half a bar, silently.
  const customValue = parseLocaleNumber(customText);
  const customValid = Number.isFinite(customValue) && customValue >= 0;
  const customTouched = customText.trim().length > 0;

  const openCustom = () => {
    // Seed from the bar that is on screen, so the field opens on the value the
    // athlete can see rather than empty. Bare, because the unit is in the title.
    setCustomText(isCustom ? formatUnitValue(bar, unit, locale) : '');
    setCustomOpen(true);
  };

  // Committed on blur / submit, never per keystroke: `onSelectBar` persists and
  // re-cuts the summary, and a half-typed "5" on the way to "54" would put a
  // 5 kg bar under the athlete's hands for as long as it takes to type the 4.
  // Same shape as the maxes editor, which parses on commit for the same reason.
  const commitCustom = () => {
    if (!customValid) return;
    onSelectBar(customValue);
  };

  const selectPreset = (value: number) => {
    setCustomOpen(false);
    onSelectBar(value);
  };

  return (
    <View style={styles.configCard}>
      <Text style={styles.sectionLabel}>{barLabel}</Text>
      <View accessibilityRole="radiogroup" accessibilityLabel={barLabel}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.chipRow}
          keyboardShouldPersistTaps="handled"
        >
          {barOptions.map((value) => (
            <Chip
              key={value}
              theme={theme}
              role="radio"
              // Riquadro, non pillola: è la forma che il mockup approvato
              // disegna (border-radius 8px) ed è quella che avevano prima.
              shape="box"
              label={formatUnitValue(value, unit, locale)}
              // ⭐ Bare on screen, but SPOKEN with its unit — the same split the
              // ± buttons and the compose chips already make. Taking the unit
              // out of the visible chip was his call; letting the spoken one
              // lose it too would not be, because a reader that swipes option
              // to option, or lands back mid-list, does not get the group's
              // title re-announced on every platform. Raised by the a11y audit:
              // these were the only controls in the block still relying on it.
              accessibilityLabel={t('mobile.plates.barOptionLabel', {
                weight: formatUnitWeight(value, unit, locale, unitLabels),
              })}
              selected={bar === value}
              onPress={() => selectPreset(value)}
            />
          ))}
          {/* Il chip «personalizzata». Quando una barra fuori lista è attiva
              PORTA IL SUO NUMERO ed è selezionato — altrimenti questa riga non
              avrebbe nessun chip acceso, che è ciò che succede oggi a una barra
              EZ da 7 kg letta su una riga in libbre (15,4). Il valore sta nel
              `label` e non in `sublabel`: quella prop è una deroga dichiarata a
              un solo chiamante, con la promessa di cancellarla se quel
              chiamante se ne va — appenderle un secondo significato la
              trasformerebbe in un primitivo che non si può più togliere. */}
          <Chip
            theme={theme}
            role="radio"
            shape="box"
            label={
              isCustom
                ? formatUnitValue(bar, unit, locale)
                : t('mobile.plates.barCustom')
            }
            accessibilityLabel={t('mobile.plates.barCustomLabel')}
            selected={isCustom}
            expanded={customOpen}
            onPress={openCustom}
          />
        </ScrollView>
      </View>

      {customOpen ? (
        <>
          <View style={styles.customBarRow}>
            <TextField
              theme={theme}
              numeric
              autoFocus
              style={styles.customBarField}
              value={customText}
              onChangeText={setCustomText}
              onEndEditing={commitCustom}
              onSubmitEditing={commitCustom}
              onBlur={commitCustom}
              invalid={customTouched && !customValid}
              placeholder={formatUnitValue(bar, unit, locale)}
              accessibilityLabel={t('mobile.plates.barCustomLabel')}
              returnKeyType="done"
            />
          </View>
          {customTouched && !customValid ? (
            <Text style={styles.fieldError}>{t('mobile.plates.invalid')}</Text>
          ) : null}
        </>
      ) : null}

      <Text style={styles.sectionLabel}>
        {t('mobile.plates.inventoryTitle', { unit: unitLabel })}
      </Text>
      {activeDenominations(inventory, unit, { withDefaults: true }).map((size) => {
        const pairs = pairsBySize.get(size) ?? 0;
        // Bare on screen (the unit is in the title above), with unit when
        // SPOKEN (a screen reader reads these rows out of context).
        const sizeLabel = formatUnitValue(size, unit, locale);
        const sizeSpoken = formatUnitWeight(size, unit, locale, unitLabels);
        return (
          <View key={size} style={styles.inventoryRow}>
            <Text style={styles.inventoryKg}>{sizeLabel}</Text>
            <Pressable
              accessibilityRole="button"
              // The WEIGHT — number and unit as one token — goes INSIDE the
              // string, not concatenated onto it: a screen reader reads these
              // rows out of context, so eight bare "remove a pair"
              // announcements are eight identical ones — and German puts the
              // verb last ("Ein Paar 25 kg entfernen"), which no amount of JS
              // concatenation can produce.
              accessibilityLabel={t('mobile.plates.pairsDecrement', {
                weight: sizeSpoken,
              })}
              onPress={() => onSetPairs(size, pairs - 1)}
              // hitSlop reaches the ≥44 floor on native, where the box stays
              // 36; on web-rnw hitSlop is ignored entirely and the floor comes
              // from `webTapFloor` in stepButton instead. Both halves needed.
              hitSlop={8}
              style={({ pressed }) => [
                styles.stepButton,
                pressed && styles.stepButtonPressed,
                // Boy-scout: this button has always been disabled at zero and
                // has never LOOKED it. The compose row below grew the same
                // control, and two minus signs fading differently would read
                // as two different meanings.
                pairs <= 0 && styles.stepButtonDisabled,
              ]}
              disabled={pairs <= 0}
            >
              <Text style={styles.stepButtonText}>−</Text>
            </Pressable>
            <Text style={styles.inventoryPairs}>
              {t('mobile.plates.pairs', { count: pairs })}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('mobile.plates.pairsIncrement', {
                weight: sizeSpoken,
              })}
              onPress={() => onSetPairs(size, pairs + 1)}
              // hitSlop reaches the ≥44 floor on native, where the box stays
              // 36; on web-rnw hitSlop is ignored entirely and the floor comes
              // from `webTapFloor` in stepButton instead. Both halves needed.
              hitSlop={8}
              style={({ pressed }) => [
                styles.stepButton,
                pressed && styles.stepButtonPressed,
              ]}
            >
              <Text style={styles.stepButtonText}>+</Text>
            </Pressable>
          </View>
        );
      })}
    </View>
  );
}
